import React, {
  createContext,
  useContext,
  useCallback,
} from 'react';
import {
  isKeycloakEnabled,
  loginWithKeycloak,
  loginAsInternalUser,
  loginAsExternalUser,
  logoutKeycloak,
  getBearerToken,
  hasRole,
  hasAnyRole,
  getKeycloakUser,
} from '../utils/keycloakAuth';

/**
 * KeycloakAuthContext — Provides Keycloak utility functions
 *
 * Responsibilities:
 * 1. Expose login/logout/token/role utility functions to the component tree
 * 2. Provide safe no-op defaults when Keycloak is not enabled
 *
 * Note:
 * - The auth callback flow (token exchange, user info retrieval) is handled by the LoginKeycloak component
 * - This Provider no longer manages initialization state or auth state
 * - Auth state is determined by useUser()'s isLogged
 */
const KeycloakAuthContext = createContext(null);

/**
 * Safe default values when Keycloak is not enabled
 */
const DISABLED_CONTEXT_VALUE = {
  isEnabled: false,
  loginWithKeycloak: null,
  loginAsInternalUser: null,
  loginAsExternalUser: null,
  logout: null,
  getBearerToken: null,
  hasRole: () => false,
  hasAnyRole: () => false,
  getKeycloakUser: () => null,
};

export const KeycloakAuthProvider = ({ children }) => {
  const safeLoginWithKeycloak = useCallback(async (realmKey) => {
    return loginWithKeycloak(realmKey);
  }, []);

  const safeLoginAsInternalUser = useCallback(async () => {
    return loginAsInternalUser();
  }, []);

  const safeLoginAsExternalUser = useCallback(async () => {
    return loginAsExternalUser();
  }, []);

  const safeLogout = useCallback(async () => {
    return logoutKeycloak();
  }, []);

  // When Keycloak is not enabled, render children directly
  if (!isKeycloakEnabled()) {
    return (
      <KeycloakAuthContext.Provider value={DISABLED_CONTEXT_VALUE}>
        {children}
      </KeycloakAuthContext.Provider>
    );
  }

  const value = {
    isEnabled: true,
    loginWithKeycloak: safeLoginWithKeycloak,
    loginAsInternalUser: safeLoginAsInternalUser,
    loginAsExternalUser: safeLoginAsExternalUser,
    logout: safeLogout,
    getBearerToken,
    hasRole,
    hasAnyRole,
    getKeycloakUser,
  };

  return (
    <KeycloakAuthContext.Provider value={value}>
      {children}
    </KeycloakAuthContext.Provider>
  );
};

/**
 * useKeycloak — Get Keycloak auth utility functions
 * @returns {Object} { isEnabled, loginWithKeycloak, loginAsInternalUser, loginAsExternalUser, logout, getBearerToken, hasRole, hasAnyRole, getKeycloakUser }
 */
export const useKeycloak = () => {
  const context = useContext(KeycloakAuthContext);
  return context || DISABLED_CONTEXT_VALUE;
};

export default KeycloakAuthContext;
